/**
 * @fileoverview Estilos das ferramentas de Super Admin da conexão.
 * Tokens alinhados ao ConfirmationModal (superfície, texto, borda), neutros:
 * seleção em grafite, vermelho só em ação destrutiva e aviso de impacto.
 * Modo claro com contraste real nos campos (borda nítida + anel de foco).
 */

import { makeStyles } from "@material-ui/core/styles";

export const tokensOf = (theme) => {
	const dark = theme.palette.type === "dark";
	return {
		dark,
		surface: dark ? "#0f172a" : "#ffffff",
		surfaceAlt: dark ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
		hover: dark ? "rgba(255, 255, 255, 0.05)" : "rgba(15, 23, 42, 0.04)",
		selected: dark ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.06)",
		textPrimary: dark ? "#f8fafc" : "#111827",
		textSecondary: dark ? "rgba(248, 250, 252, 0.74)" : "#374151",
		textMuted: dark ? "rgba(248, 250, 252, 0.52)" : "#6b7280",
		line: dark ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.08)",
		lineStrong: dark ? "rgba(255, 255, 255, 0.16)" : "rgba(15, 23, 42, 0.16)",
		inputBg: dark ? "rgba(255, 255, 255, 0.04)" : "#ffffff",
		inputBorder: dark ? "rgba(255, 255, 255, 0.16)" : "rgba(0, 0, 0, 0.18)",
		focusRing: dark ? "0 0 0 3px rgba(148, 163, 184, 0.28)" : "0 0 0 3px rgba(15, 23, 42, 0.12)",
		// Campo focado: anel de 1px, como os campos do resto do app (o de cima é o foco do teclado nos botões).
		inputFocusRing: dark ? "0 0 0 1px rgba(148, 163, 184, 0.28)" : "0 0 0 1px rgba(15, 23, 42, 0.12)",
		ink: dark ? "#e2e8f0" : "#111827",
		inkText: dark ? "#0f172a" : "#ffffff",
		track: dark ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.08)",
		danger: dark ? "#fca5a5" : "#b91c1c",
		dangerSolid: "#dc2626",
		dangerSolidHover: "#b91c1c",
		dangerSoftBg: dark ? "rgba(248, 113, 113, 0.10)" : "#fef2f2",
		dangerSoftBorder: dark ? "rgba(248, 113, 113, 0.28)" : "rgba(220, 38, 38, 0.22)",
		dangerText: dark ? "#fecaca" : "#7f1d1d",
		transition: "all 160ms cubic-bezier(0.4, 0, 0.2, 1)",
	};
};

const useStyles = makeStyles((theme) => {
	const k = tokensOf(theme);
	const xs = theme.breakpoints.down("xs");
	const button = {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
		borderRadius: 10,
		fontFamily: "inherit",
		fontSize: 13.5,
		fontWeight: 600,
		cursor: "pointer",
		whiteSpace: "nowrap",
		transition: k.transition,
		"&:disabled": { opacity: 0.45, cursor: "not-allowed" },
	};
	const field = {
		height: 40,
		width: "100%",
		boxSizing: "border-box",
		padding: "0 12px",
		borderRadius: 10,
		border: `1px solid ${k.inputBorder}`,
		background: k.inputBg,
		color: k.textPrimary,
		fontFamily: "inherit",
		fontSize: 14,
		outline: "none",
		colorScheme: k.dark ? "dark" : "light",
		transition: k.transition,
		"&:focus": { borderColor: k.ink, boxShadow: k.inputFocusRing },
		"&::placeholder": { color: k.textMuted },
	};

	return {
		// ── Diálogo principal ──────────────────────────────────────────
		paper: {
			width: 980,
			maxWidth: "calc(100% - 32px)",
			height: "min(88vh, 740px)",
			display: "flex",
			flexDirection: "column",
			borderRadius: 20,
			overflow: "hidden",
			background: k.surface,
			color: k.textPrimary,
			boxShadow: k.dark
				? "0 24px 48px -12px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.06)"
				: "0 24px 48px -12px rgba(15, 23, 42, 0.28), 0 0 0 1px rgba(15, 23, 42, 0.05)",
			[xs]: { width: "100%", maxWidth: "100%", height: "100%", borderRadius: 0 },
		},
		backdrop: { backgroundColor: k.dark ? "rgba(2, 6, 23, 0.72)" : "rgba(15, 23, 42, 0.48)" },
		header: {
			display: "flex",
			alignItems: "center",
			gap: 14,
			padding: "18px 64px 16px 24px",
			borderBottom: `1px solid ${k.line}`,
			position: "relative",
			flexShrink: 0,
			[xs]: { padding: "14px 56px 12px 16px" },
		},
		headerIcon: {
			width: 40,
			height: 40,
			borderRadius: 12,
			border: `1px solid ${k.line}`,
			background: k.surfaceAlt,
			color: k.textPrimary,
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			flexShrink: 0,
		},
		eyebrow: {
			fontSize: 10,
			fontWeight: 700,
			letterSpacing: "0.14em",
			textTransform: "uppercase",
			color: k.textMuted,
			marginBottom: 2,
		},
		title: {
			margin: 0,
			fontSize: 17,
			fontWeight: 700,
			letterSpacing: "-0.01em",
			color: k.textPrimary,
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
		},
		subtitle: { fontSize: 12.5, color: k.textMuted, marginTop: 2 },
		closeBtn: {
			position: "absolute",
			top: 16,
			right: 16,
			width: 32,
			height: 32,
			borderRadius: 10,
			border: "none",
			background: "transparent",
			color: k.textMuted,
			cursor: "pointer",
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			transition: k.transition,
			"&:hover": { background: k.hover, color: k.textPrimary },
			[xs]: { top: 12, right: 12 },
		},
		body: {
			display: "flex",
			flex: 1,
			minHeight: 0,
			[xs]: { flexDirection: "column" },
		},
		nav: {
			width: 232,
			flexShrink: 0,
			display: "flex",
			flexDirection: "column",
			gap: 4,
			padding: "14px 10px",
			borderRight: `1px solid ${k.line}`,
			background: k.surfaceAlt,
			[xs]: {
				width: "auto",
				flexDirection: "row",
				overflowX: "auto",
				padding: "8px 10px",
				borderRight: "none",
				borderBottom: `1px solid ${k.line}`,
			},
		},
		navItem: {
			display: "flex",
			alignItems: "center",
			gap: 10,
			width: "100%",
			padding: "10px 12px",
			borderRadius: 10,
			border: "1px solid transparent",
			background: "transparent",
			color: k.textSecondary,
			fontFamily: "inherit",
			fontSize: 13.5,
			fontWeight: 500,
			textAlign: "left",
			cursor: "pointer",
			transition: k.transition,
			"&:hover": { background: k.hover, color: k.textPrimary },
			[xs]: { width: "auto", flexShrink: 0, whiteSpace: "nowrap", padding: "8px 12px" },
		},
		navItemActive: {
			background: k.selected,
			borderColor: k.line,
			color: k.textPrimary,
			fontWeight: 600,
		},
		navBadge: {
			marginLeft: "auto",
			width: 8,
			height: 8,
			borderRadius: "50%",
			background: k.ink,
			animation: "$pulse 1.6s ease-in-out infinite",
			[xs]: { marginLeft: 4 },
		},
		navNote: {
			marginTop: "auto",
			display: "flex",
			gap: 8,
			padding: "12px 10px 4px",
			fontSize: 11.5,
			lineHeight: 1.5,
			color: k.textMuted,
			"& svg": { flexShrink: 0, marginTop: 1 },
			[xs]: { display: "none" },
		},
		content: {
			flex: 1,
			minWidth: 0,
			overflowY: "auto",
			padding: "22px 26px 26px",
			[xs]: { padding: 16 },
		},
		centered: {
			height: "100%",
			minHeight: 240,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			color: k.textMuted,
		},

		// ── Blocos comuns ─────────────────────────────────────────────
		intro: { fontSize: 13.5, lineHeight: 1.55, color: k.textSecondary, margin: "0 0 20px" },
		section: { marginBottom: 22 },
		sectionTitle: {
			display: "flex",
			alignItems: "baseline",
			justifyContent: "space-between",
			gap: 12,
			fontSize: 11,
			fontWeight: 700,
			letterSpacing: "0.08em",
			textTransform: "uppercase",
			color: k.textMuted,
			marginBottom: 10,
		},
		hint: { fontSize: 12.5, lineHeight: 1.5, color: k.textMuted, marginTop: 8 },
		// Colunas pelo espaço disponível: entre 600 e ~880 px as três colunas
		// fixas eram mais estreitas que o rótulo + contagem.
		optionGrid: {
			display: "grid",
			gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
			gap: 10,
		},
		option: {
			display: "flex",
			alignItems: "center",
			gap: 10,
			padding: "11px 14px",
			borderRadius: 12,
			border: `1px solid ${k.inputBorder}`,
			background: k.inputBg,
			color: k.textPrimary,
			fontFamily: "inherit",
			textAlign: "left",
			cursor: "pointer",
			transition: k.transition,
			"&:hover": { borderColor: k.lineStrong, background: k.hover },
			"&:focus-visible": { outline: "none", boxShadow: k.focusRing },
		},
		optionSelected: {
			borderColor: k.ink,
			boxShadow: `inset 0 0 0 1px ${k.ink}`,
			"&:hover": { borderColor: k.ink },
		},
		optionLabel: { fontSize: 13.5, fontWeight: 600 },
		optionCount: {
			marginLeft: "auto",
			fontSize: 13,
			fontVariantNumeric: "tabular-nums",
			color: k.textSecondary,
		},
		check: {
			width: 18,
			height: 18,
			borderRadius: 5,
			border: `1.5px solid ${k.inputBorder}`,
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			flexShrink: 0,
			color: k.inkText,
			transition: k.transition,
		},
		checkOn: { background: k.ink, borderColor: k.ink },
		toggleRow: {
			display: "inline-flex",
			alignItems: "center",
			gap: 10,
			marginTop: 12,
			padding: "4px 0",
			border: "none",
			background: "transparent",
			color: k.textPrimary,
			fontFamily: "inherit",
			fontSize: 13.5,
			cursor: "pointer",
			"&:focus-visible": { outline: "none", boxShadow: k.focusRing, borderRadius: 6 },
		},
		toggleCount: { color: k.textMuted, fontSize: 12.5 },
		segmented: {
			display: "inline-flex",
			flexWrap: "wrap",
			gap: 2,
			padding: 3,
			borderRadius: 12,
			background: k.dark ? "rgba(255, 255, 255, 0.05)" : "#eef1f5",
			[xs]: { display: "flex" },
		},
		segment: {
			padding: "7px 14px",
			borderRadius: 9,
			border: "none",
			background: "transparent",
			color: k.textSecondary,
			fontFamily: "inherit",
			fontSize: 13,
			fontWeight: 500,
			cursor: "pointer",
			whiteSpace: "nowrap",
			transition: k.transition,
			"&:hover": { color: k.textPrimary },
			"&:focus-visible": { outline: "none", boxShadow: k.focusRing },
			[xs]: { flex: "1 0 auto" },
		},
		segmentActive: {
			background: k.dark ? "rgba(255, 255, 255, 0.14)" : "#ffffff",
			color: k.textPrimary,
			fontWeight: 600,
			boxShadow: k.dark ? "none" : "0 1px 2px rgba(15, 23, 42, 0.14)",
		},
		dateRow: {
			display: "flex",
			gap: 12,
			marginTop: 12,
			maxWidth: 420,
			[xs]: { flexDirection: "column", maxWidth: "none" },
		},
		dateField: { flex: 1, display: "flex", flexDirection: "column", gap: 6 },
		label: { fontSize: 12.5, fontWeight: 600, color: k.textPrimary },
		input: field,
		inputError: { borderColor: k.danger, "&:focus": { borderColor: k.danger } },

		// ── Prévia ────────────────────────────────────────────────────
		preview: {
			border: `1px solid ${k.line}`,
			borderRadius: 14,
			padding: "16px 18px",
			background: k.surfaceAlt,
		},
		previewHead: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			gap: 12,
			marginBottom: 14,
		},
		previewTitle: { fontSize: 13.5, fontWeight: 700, color: k.textPrimary },
		previewMeta: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: k.textMuted },
		iconBtn: {
			width: 30,
			height: 30,
			borderRadius: 8,
			border: "none",
			background: "transparent",
			color: k.textMuted,
			cursor: "pointer",
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			transition: k.transition,
			"&:hover:not(:disabled)": { background: k.hover, color: k.textPrimary },
			"&:disabled": { opacity: 0.4, cursor: "default" },
		},
		stats: {
			display: "grid",
			gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
			gap: 14,
			transition: "opacity 160ms ease",
		},
		statsLoading: { opacity: 0.45 },
		stat: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
		statValue: {
			fontSize: 22,
			fontWeight: 700,
			letterSpacing: "-0.01em",
			fontVariantNumeric: "tabular-nums",
			color: k.textPrimary,
		},
		statLabel: { fontSize: 12, color: k.textMuted },
		statSub: { fontSize: 11.5, color: k.textSecondary },
		rangeNote: { fontSize: 12, color: k.textMuted, marginTop: 12 },
		muted: { fontSize: 13, color: k.textSecondary },

		// ── Avisos ────────────────────────────────────────────────────
		callout: {
			display: "flex",
			gap: 10,
			marginTop: 14,
			padding: "12px 14px",
			borderRadius: 12,
			fontSize: 13,
			lineHeight: 1.55,
			"& svg": { flexShrink: 0, marginTop: 2 },
			"& ul": { margin: "6px 0 0", paddingLeft: 18 },
		},
		calloutDanger: {
			background: k.dangerSoftBg,
			border: `1px solid ${k.dangerSoftBorder}`,
			color: k.dangerText,
			"& svg": { color: k.danger },
		},
		calloutNeutral: {
			background: k.surfaceAlt,
			border: `1px solid ${k.line}`,
			color: k.textSecondary,
		},

		// ── Ações ─────────────────────────────────────────────────────
		// Rodapé fixo na base da área rolável: o botão fica sempre à vista. No
		// celular volta ao fluxo normal (fixo, a nota tomaria meia tela).
		actionBar: {
			position: "sticky",
			bottom: -26,
			zIndex: 1,
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			gap: 16,
			margin: "22px -26px -26px",
			padding: "14px 26px 16px",
			borderTop: `1px solid ${k.line}`,
			background: k.surface,
			[xs]: {
				position: "static",
				margin: "18px 0 0",
				padding: "16px 0 0",
				background: "transparent",
				flexDirection: "column-reverse",
				alignItems: "stretch",
			},
		},
		actionNote: { fontSize: 12.5, lineHeight: 1.5, color: k.textMuted, maxWidth: 480 },
		btnDanger: {
			...button,
			height: 40,
			padding: "0 18px",
			border: "none",
			background: k.dangerSolid,
			color: "#ffffff",
			"&:hover:not(:disabled)": { background: k.dangerSolidHover },
		},
		// Ação que não destrói nada (resolver): sólido neutro, sem o vermelho.
		btnInk: {
			...button,
			height: 40,
			padding: "0 18px",
			border: "none",
			background: k.ink,
			color: k.inkText,
			"&:hover:not(:disabled)": { opacity: 0.88 },
		},
		btnGhost: {
			...button,
			height: 38,
			padding: "0 16px",
			border: `1px solid ${k.lineStrong}`,
			background: "transparent",
			color: k.textSecondary,
			"&:hover:not(:disabled)": { background: k.hover, color: k.textPrimary },
		},
		btnGhostDanger: {
			...button,
			height: 38,
			padding: "0 16px",
			border: `1px solid ${k.dangerSoftBorder}`,
			background: "transparent",
			color: k.danger,
			"&:hover:not(:disabled)": { background: k.dangerSoftBg },
		},

		// ── Progresso ─────────────────────────────────────────────────
		progress: {
			border: `1px solid ${k.line}`,
			borderRadius: 16,
			padding: "20px 22px",
			background: k.surfaceAlt,
		},
		progressHead: {
			display: "flex",
			alignItems: "flex-start",
			justifyContent: "space-between",
			gap: 12,
			flexWrap: "wrap",
		},
		progressTitle: { fontSize: 16, fontWeight: 700, color: k.textPrimary, margin: 0 },
		progressFilters: { fontSize: 12.5, color: k.textMuted, marginTop: 4 },
		progressNumbers: {
			marginTop: 18,
			fontSize: 28,
			fontWeight: 700,
			letterSpacing: "-0.02em",
			fontVariantNumeric: "tabular-nums",
			color: k.textPrimary,
		},
		progressPct: { fontSize: 15, fontWeight: 600, color: k.textSecondary, marginLeft: 8 },
		bar: {
			height: 8,
			borderRadius: 999,
			background: k.track,
			overflow: "hidden",
			marginTop: 12,
			position: "relative",
		},
		barFill: {
			height: "100%",
			borderRadius: 999,
			background: k.ink,
			transition: "width 400ms ease",
		},
		barIndeterminate: {
			position: "absolute",
			top: 0,
			bottom: 0,
			width: "35%",
			borderRadius: 999,
			background: k.ink,
			animation: "$slide 1.3s ease-in-out infinite",
		},
		progressStats: {
			display: "grid",
			gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
			gap: 14,
			marginTop: 20,
		},
		progressMeta: { fontSize: 12.5, lineHeight: 1.6, color: k.textMuted, marginTop: 18 },
		errorBox: {
			marginTop: 14,
			padding: "10px 12px",
			borderRadius: 10,
			background: k.dangerSoftBg,
			border: `1px solid ${k.dangerSoftBorder}`,
			color: k.dangerText,
			fontSize: 12.5,
			lineHeight: 1.5,
			wordBreak: "break-word",
		},
		progressActions: { display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" },

		// ── Status ────────────────────────────────────────────────────
		badge: {
			display: "inline-flex",
			alignItems: "center",
			gap: 6,
			padding: "3px 10px",
			borderRadius: 999,
			border: `1px solid ${k.lineStrong}`,
			fontSize: 11.5,
			fontWeight: 600,
			color: k.textSecondary,
			whiteSpace: "nowrap",
		},
		badgeDot: { width: 7, height: 7, borderRadius: "50%", background: "currentColor" },
		badgeActive: { color: k.textPrimary, borderColor: k.ink },
		badgeDotPulse: { animation: "$pulse 1.6s ease-in-out infinite" },
		badgeDone: { color: k.textPrimary },
		badgeMuted: { color: k.textMuted, borderColor: k.line },
		badgeDanger: { color: k.danger, borderColor: k.dangerSoftBorder },

		// ── Histórico ─────────────────────────────────────────────────
		historyList: { display: "flex", flexDirection: "column", gap: 10 },
		historyItem: {
			display: "grid",
			gridTemplateColumns: "minmax(0, 1fr) auto",
			columnGap: 14,
			rowGap: 4,
			padding: "12px 14px",
			borderRadius: 12,
			border: `1px solid ${k.line}`,
			background: k.surfaceAlt,
			[xs]: { gridTemplateColumns: "1fr" },
		},
		historyTitle: {
			display: "flex",
			alignItems: "center",
			gap: 8,
			flexWrap: "wrap",
			fontSize: 13.5,
			fontWeight: 600,
			color: k.textPrimary,
		},
		historyFilters: { fontSize: 12.5, color: k.textSecondary },
		historyMeta: { fontSize: 12, color: k.textMuted },
		historyCounts: {
			textAlign: "right",
			alignSelf: "center",
			fontSize: 13,
			fontWeight: 600,
			fontVariantNumeric: "tabular-nums",
			color: k.textPrimary,
			[xs]: { textAlign: "left" },
		},
		historyCountsSub: { display: "block", fontSize: 11.5, fontWeight: 400, color: k.textMuted },
		historyError: { gridColumn: "1 / -1", fontSize: 12, color: k.danger, wordBreak: "break-word" },
		historyActions: { gridColumn: "1 / -1", marginTop: 6 },
		loadMore: { display: "flex", justifyContent: "center", marginTop: 14 },
		empty: { textAlign: "center", padding: "48px 16px", fontSize: 13.5, color: k.textMuted },

		// ── Confirmação ───────────────────────────────────────────────
		// Rola por dentro em tela baixa (notebook com zoom, celular deitado):
		// frase e botão nunca ficam fora do alcance.
		confirmPaper: {
			width: 480,
			maxWidth: "calc(100% - 32px)",
			borderRadius: 20,
			overflowX: "hidden",
			overflowY: "auto",
			background: k.surface,
			color: k.textPrimary,
			boxShadow: k.dark
				? "0 24px 48px -12px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.06)"
				: "0 24px 48px -12px rgba(15, 23, 42, 0.28), 0 0 0 1px rgba(15, 23, 42, 0.05)",
		},
		confirmHead: {
			display: "flex",
			gap: 14,
			alignItems: "flex-start",
			padding: "22px 56px 0 22px",
			position: "relative",
		},
		confirmIcon: {
			width: 44,
			height: 44,
			borderRadius: 14,
			background: k.dangerSoftBg,
			border: `1px solid ${k.dangerSoftBorder}`,
			color: k.danger,
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			flexShrink: 0,
		},
		// Resolver não destrói nada: o ícone fica neutro, como o do cabeçalho.
		confirmIconNeutral: {
			background: k.surfaceAlt,
			border: `1px solid ${k.line}`,
			color: k.textPrimary,
		},
		confirmTitle: { margin: 0, fontSize: 17, fontWeight: 700, color: k.textPrimary, lineHeight: 1.35 },
		confirmBody: { padding: "14px 22px 18px", fontSize: 13.5, lineHeight: 1.55, color: k.textSecondary },
		summaryBox: {
			margin: "12px 0 4px",
			padding: "10px 12px",
			borderRadius: 12,
			border: `1px solid ${k.line}`,
			background: k.surfaceAlt,
			color: k.textPrimary,
			fontSize: 13,
			"& ul": { margin: 0, paddingLeft: 18 },
		},
		fieldBlock: { display: "flex", flexDirection: "column", gap: 6, marginTop: 14 },
		phrase: {
			padding: "1px 7px",
			borderRadius: 6,
			background: k.dark ? "rgba(255, 255, 255, 0.10)" : "#eef1f5",
			color: k.textPrimary,
			fontWeight: 700,
			whiteSpace: "nowrap",
		},
		confirmActions: {
			display: "flex",
			justifyContent: "flex-end",
			gap: 8,
			padding: "14px 22px 18px",
			borderTop: `1px solid ${k.line}`,
			background: k.surfaceAlt,
		},

		spin: { animation: "$spin 0.8s linear infinite" },
		"@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } },
		"@keyframes pulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.35 } },
		"@keyframes slide": { "0%": { left: "-35%" }, "100%": { left: "100%" } },
	};
});

export default useStyles;
